import { effect, Injectable, signal } from '@angular/core';
import { ERROR_SNACKBAR, SnackbarType, SUCCESS_SNACKBAR } from '@nagel-types';
import { AssignmentRequestsService } from './assignment-requests.service';
import { RequestService, SnackbarService } from '@nagel-services';
import { protectSubscription } from '@nagel-utils';
import { TourPointDetails } from '../../models/orderDetails';
import { ReorderRequest, TourPointConfig } from '../../models/planningPageTypes';
import { DELIVERY_TOUR_POINT_TYPE, FINISH_TOUR_POINT_TYPE, PICKUP_TOUR_POINT_TYPE, RELATION_TYPE_MAP, START_TOUR_POINT_TYPE } from '../utils/tourPointsUtils';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { environment } from 'apps/nagel-cal-disposition/environment/environment';
import { transportOrderResourceEndpoint } from '../configuration/consts/endpoints';

type ReorderTourPointsRequest = {
    SourceTransportOrderTix: number;
    SourceTourpointId: number;
    DestinationTourpointId: number;
    RelationType: number;
    Mode: null;
};

const SUCCESSFUL_REORDER_MESSAGE = 'The Order has been successfully changed.';
const ERROR_FIRST_REORDER = 'Invalid moving! Unloading point can not be at first position!';
const ERROR_RESTRICTED_REORDER = 'Loading point can not be behind Unloading point!';
const ERROR_REORDER_MESSAGE = 'An error occurred while reordering.';
const shouldEmitSubscriptionOnce = true;

@Injectable({
    providedIn: 'root'
})
export class ReorderDragAndDropService {
    private pendingReorder = signal<ReorderRequest<TourPointDetails | TourPointConfig> | null>(null);

    constructor(
        private assignmentRequestsService: AssignmentRequestsService,
        private snackbarService: SnackbarService,
        private requestService: RequestService
    ) {
        effect(() => {
            const request = this.pendingReorder();


            if (request) {
                const {
                    tourpointId,
                    relationType,
                    transportOrderId,
                    destinationTourPointId,
                    updatedTourPoints,
                    onSuccess
                } = request;

                const reorderTourpointsUrl = `${environment.apiUrl}${transportOrderResourceEndpoint}/${transportOrderId}/tourpoints/reorder`;

                const requestPayload = {
                    SourceTourpointId: tourpointId,
                    DestinationTourpointId: destinationTourPointId,
                    RelationType: relationType,
                    Mode: null
                };

                protectSubscription(
                    this.requestService.putRequest<ReorderTourPointsRequest, { isTourpointMoved: boolean }>(reorderTourpointsUrl, requestPayload),
                    (data) => this.onSuccessfulRetriaval(data, updatedTourPoints, onSuccess),
                    (error) => this.showSnackbar((error as Error).message, ERROR_SNACKBAR),
                    shouldEmitSubscriptionOnce
                );
            }
        });
    }

    dropAssigmentLegsReorder(
        transportOrderId: number,
        legId: string,
        newOrder: number,
        onSuccess: () => void
    ) {
        protectSubscription(
            this.assignmentRequestsService.reorderAssignmentLegs(transportOrderId, legId, newOrder),
            () => {
                this.showSnackbar(SUCCESSFUL_REORDER_MESSAGE, SUCCESS_SNACKBAR);
                onSuccess();
            },
            (error) => this.showSnackbar((error as Error).message, ERROR_SNACKBAR),
            shouldEmitSubscriptionOnce
        );
    }

    dropTourPointReorder<T extends TourPointDetails | TourPointConfig>(
        event: CdkDragDrop<T[]>,
        onSuccess: (tourPoints: T[]) => void,
        droppedTourPoints: T[] = [],
        transportOrderId?: number,
    ) {
        const { updatedTourPoints, targetIndex } = this.reorder(droppedTourPoints, event.previousIndex, event.currentIndex);

        if (!this.hasPickupBeforeDelivery(updatedTourPoints)) {
            this.showSnackbar(ERROR_RESTRICTED_REORDER, ERROR_SNACKBAR);

            return;
        }

        if (this.isFirstDeliveryPoint(updatedTourPoints)) {
            this.showSnackbar(ERROR_FIRST_REORDER, ERROR_SNACKBAR);

            return;
        }

        const { relationType, destinationTourPointId } = this.getReorderPosition(updatedTourPoints, targetIndex);

        if (targetIndex !== event.previousIndex) {
            this.pendingReorder.set({
                tourpointId: updatedTourPoints[targetIndex].tourpointId,
                relationType,
                transportOrderId: transportOrderId || (updatedTourPoints[targetIndex] as TourPointDetails).transportOrderId,
                destinationTourPointId,
                updatedTourPoints,
                onSuccess: onSuccess as (tourPoints: (TourPointDetails | TourPointConfig)[]) => void
            });
        }
    }

    private isFirstDeliveryPoint<T extends TourPointDetails | TourPointConfig>(tourPoints: T[]): boolean {
        return (
            tourPoints[0].type === DELIVERY_TOUR_POINT_TYPE ||
            (tourPoints[0].type === START_TOUR_POINT_TYPE && tourPoints[1].type === DELIVERY_TOUR_POINT_TYPE)
        );
    }

    private getReorderPosition(
        tourPoints: (TourPointDetails | TourPointConfig)[],
        targetIndex: number
    ) {
        let relationType: number;
        let destinationTourPointId: number;

        if (targetIndex === 0) {
            relationType = RELATION_TYPE_MAP.first;
            destinationTourPointId = 0;
        } else if (targetIndex === tourPoints.length - 1) {
            relationType = RELATION_TYPE_MAP.last;
            destinationTourPointId = 0;
        } else {
            relationType = RELATION_TYPE_MAP.before;
            destinationTourPointId = tourPoints[targetIndex + 1].tourpointId;
        }

        return { relationType, destinationTourPointId };
    }

    private hasTypeTourPoint(tourPoints: (TourPointDetails | TourPointConfig)[], type: number) {
        return tourPoints.some(tourPoint => tourPoint.type === type);
    }

    private reorder(tourPoints: (TourPointDetails | TourPointConfig)[], previousIndex: number, currentIndex: number) {
        const updatedTourPoints = [...tourPoints];
        const hasStart = this.hasTypeTourPoint(updatedTourPoints, START_TOUR_POINT_TYPE);
        const hasFinish = this.hasTypeTourPoint(updatedTourPoints, FINISH_TOUR_POINT_TYPE);
        let targetIndex = currentIndex;

        if (hasStart && currentIndex === 0) {
            targetIndex = currentIndex + 1;
        } else if (hasFinish && currentIndex === updatedTourPoints.length - 1) {
            targetIndex = currentIndex - 1;
        }

        moveItemInArray(updatedTourPoints, previousIndex, targetIndex);

        return { updatedTourPoints, targetIndex };
    }

    private isTourPointDetails(point: TourPointDetails | TourPointConfig) {
        return Array.isArray((point as TourPointDetails).legIds);
    }

    private isTourPointConfig(point: TourPointDetails | TourPointConfig) {
        return Array.isArray((point as TourPointConfig).legCards);
    }

    private getLegIds(point: TourPointDetails | TourPointConfig): string[] {
        let legIds: string[] = [];

        if (this.isTourPointDetails(point)) {
            legIds = (point as TourPointDetails).legIds;
        } else if (this.isTourPointConfig(point)) {
            legIds = (point as TourPointConfig).legCards.map(card => card.legId);
        }

        return legIds;
    }

    private hasPickupBeforeDelivery<T extends TourPointDetails | TourPointConfig>(tourPoints: T[]): boolean {
        const loadedLots = new Set<string>();
        let isValid = true;

        for (const tourPoint of tourPoints) {
            const legIds = this.getLegIds(tourPoint);

            for (const legId of legIds) {
                if (!this.isLotAssignmentOrderValid(tourPoint.type, legId, loadedLots)) {
                    isValid = false;
                }
            }
        }

        return isValid;
    }

    private isLotAssignmentOrderValid(type: number, lotAssignmentId: string, loadedLots: Set<string>) {
        let isValid = true;

        if (type === PICKUP_TOUR_POINT_TYPE) {
            loadedLots.add(lotAssignmentId);
        } else if (type === DELIVERY_TOUR_POINT_TYPE && !loadedLots.has(lotAssignmentId)) {
            isValid = false;
        }

        return isValid;
    }

    showSnackbar(message: string, type: SnackbarType) {
        this.snackbarService.pushSnackbar({
            message,
            action: 'OK',
            type
        });
    }

    private onSuccessfulRetriaval<T extends TourPointDetails | TourPointConfig>(
        data: { isTourpointMoved: boolean },
        updatedTourPoints: T[],
        onSuccess: (tourPoints: T[]) => void
    ) {
        if (!data.isTourpointMoved) {
            this.showSnackbar(ERROR_REORDER_MESSAGE, ERROR_SNACKBAR);
            return;
        }

        this.showSnackbar(SUCCESSFUL_REORDER_MESSAGE, SUCCESS_SNACKBAR);
        onSuccess(updatedTourPoints);
    }
}